import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';

const SORT_OPTIONS = [
  { id: 'newest', labelKey: 'pages.tools.historySortNewest', fallback: 'Newest first', icon: 'arrow-down-wide-short', iosIcon: 'arrow.down' },
  { id: 'oldest', labelKey: 'pages.tools.historySortOldest', fallback: 'Oldest first', icon: 'arrow-up-wide-short',   iosIcon: 'arrow.up' },
];

/**
 * Compact sort selector for the tool history page header. Only two options
 * for now (newest / oldest); the trigger shows the current selection so the
 * user always knows the current ordering at a glance.
 */
function ToolHistorySortDropdown({ activeSort = 'newest', onChange }) {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const active = SORT_OPTIONS.find(o => o.id === activeSort) || SORT_OPTIONS[0];

  const styles = useMemo(() => StyleSheet.create({
    trigger: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
  }), [colors]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Pressable
          style={({ hovered }) => [
            styles.trigger,
            hovered && { backgroundColor: colors.solid_color_secondary },
          ]}
          accessibilityRole='button'
          accessibilityLabel={t('pages.tools.historySort') || 'Sort'}
        >
          <Icon name={active.icon} size={12} color={colors.text_color_primary} />
          <Text size='s' weight='medium' color='primary'>
            {t(active.labelKey) || active.fallback}
          </Text>
          <Icon name='chevron-down' size={9} color={colors.text_color_tertiary} />
        </Pressable>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        align='end'
        sideOffset={6}
        style={{ minWidth: 200 }}
      >
        {SORT_OPTIONS.map(opt => (
          Platform.OS === 'web' ? (
            <DropdownMenuItem
              key={opt.id}
              icon={opt.icon}
              iosIcon={opt.iosIcon}
              title={t(opt.labelKey) || opt.fallback}
              isActive={activeSort === opt.id}
              onSelect={() => onChange?.(opt.id)}
            />
          ) : (
            // Native menus only read children whose element type is
            // DropdownMenu.Item, so the wrapper is web-only here.
            <DropdownMenu.Item key={opt.id} onSelect={() => onChange?.(opt.id)}>
              {(activeSort === opt.id || opt.iosIcon) && (
                <DropdownMenu.ItemIcon
                  ios={{ name: activeSort === opt.id ? 'checkmark' : opt.iosIcon }}
                />
              )}
              <DropdownMenu.ItemTitle>{t(opt.labelKey) || opt.fallback}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          )
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default React.memo(ToolHistorySortDropdown);
